/**
 * Task #2652 — Pannello Custom AI condiviso (shared ui-frontend).
 *
 * Estratto da `frontend/src/components/ai/CustomAITab.tsx` (Task #1976) e reso
 * agnostico al boundary HTTP via `CustomAIDeps` (api/basePath/notify/formatError):
 * il tenant lo monta con `apiClient` + `/custom-ai`, il master con `apiClient`
 * + `/master/custom-ai/<tenantId>`.
 */
import { useCallback, useEffect, useState } from 'react';
import { Loader2, Plus, RefreshCw, Trash2, Zap, Search, Server, Euro } from 'lucide-react';
import { EndpointWizard } from './EndpointWizard';
import { HealthCard } from './HealthCard';
import { CapabilityChecklist } from './CapabilityChecklist';
import type {
  CustomAIDeps,
  CustomLlmEndpointRow,
  CustomLlmModelRow,
  CustomEndpointPreset,
  DiscoveredModel,
  HealthItem,
  ProbeReport,
} from './types';

interface PresetsResponse {
  presets: CustomEndpointPreset[];
}
interface EndpointsResponse {
  endpoints: CustomLlmEndpointRow[];
}
interface ModelsResponse {
  models: CustomLlmModelRow[];
}
interface RegisterModelResponse {
  model: CustomLlmModelRow;
}
interface DiscoverResponse {
  ok: boolean;
  http_status: number | null;
  models: DiscoveredModel[];
  error: string | null;
}
interface HealthResponse {
  items: HealthItem[];
}

interface Props {
  deps: CustomAIDeps;
}

export function CustomAIPanel({ deps }: Props): JSX.Element {
  const { api, basePath, notify, formatError } = deps;
  const [presets, setPresets] = useState<CustomEndpointPreset[]>([]);
  const [endpoints, setEndpoints] = useState<CustomLlmEndpointRow[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [showWizard, setShowWizard] = useState<boolean>(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [models, setModels] = useState<CustomLlmModelRow[]>([]);
  const [discovered, setDiscovered] = useState<DiscoveredModel[]>([]);
  const [discovering, setDiscovering] = useState<boolean>(false);
  const [probingId, setProbingId] = useState<string | null>(null);
  const [health, setHealth] = useState<HealthItem[]>([]);
  const [priceEditId, setPriceEditId] = useState<string | null>(null);
  const [priceDraft, setPriceDraft] = useState<{ in: string; out: string }>({ in: '', out: '' });
  const [savingPrice, setSavingPrice] = useState<boolean>(false);

  const selected = endpoints.find((e) => e.id === selectedId) ?? null;

  const loadEndpoints = useCallback(async () => {
    setLoading(true);
    try {
      const [presetsRes, listRes] = await Promise.all([
        api.get<PresetsResponse>(`${basePath}/presets`),
        api.get<EndpointsResponse>(`${basePath}/endpoints`),
      ]);
      const presetsList = presetsRes.data?.presets ?? [];
      const endpointsList = listRes.data?.endpoints ?? [];
      setPresets(presetsList);
      setEndpoints(endpointsList);
      const first = endpointsList[0];
      if (!selectedId && first) {
        setSelectedId(first.id);
      }
    } catch (err) {
      console.error('[CustomAIPanel] loadEndpoints failed', err);
      notify({ title: 'Errore', description: formatError(err, 'Caricamento Custom AI fallito'), variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [api, basePath, notify, formatError, selectedId]);

  const loadModelsAndHealth = useCallback(async (endpointId: string) => {
    try {
      const [m, h] = await Promise.all([
        api.get<ModelsResponse>(`${basePath}/endpoints/${endpointId}/models`),
        api.get<HealthResponse>(`${basePath}/endpoints/${endpointId}/health?limit=20`),
      ]);
      setModels(m.data?.models ?? []);
      setHealth(h.data?.items ?? []);
    } catch (err) {
      console.error('[CustomAIPanel] loadModelsAndHealth failed', err);
      notify({ title: 'Errore', description: formatError(err, 'Caricamento dettagli endpoint fallito'), variant: 'destructive' });
    }
  }, [api, basePath, notify, formatError]);

  useEffect(() => { void loadEndpoints(); }, [loadEndpoints]);
  useEffect(() => {
    if (selectedId) void loadModelsAndHealth(selectedId);
    else { setModels([]); setHealth([]); setDiscovered([]); }
  }, [selectedId, loadModelsAndHealth]);

  async function handleDelete(id: string, name: string): Promise<void> {
    if (!window.confirm(`Eliminare l'endpoint "${name}"? Verranno rimossi anche i modelli associati.`)) return;
    try {
      await api.delete(`${basePath}/endpoints/${id}`);
      notify({ title: 'Endpoint eliminato.' });
      if (selectedId === id) setSelectedId(null);
      void loadEndpoints();
    } catch (err) {
      notify({ title: 'Errore', description: formatError(err, 'Eliminazione fallita'), variant: 'destructive' });
    }
  }

  async function handleDiscover(): Promise<void> {
    if (!selectedId) return;
    setDiscovering(true);
    setDiscovered([]);
    try {
      const res = await api.post<DiscoverResponse>(
        `${basePath}/endpoints/${selectedId}/discover-models`,
        {},
      );
      const body = res.data;
      if (body?.ok) {
        setDiscovered(body.models);
        notify({ title: `${body.models.length} modelli rilevati` });
      } else {
        notify({ title: 'Errore', description: `Discovery fallita: ${body?.error ?? 'errore sconosciuto'}`, variant: 'destructive' });
      }
      void loadModelsAndHealth(selectedId);
    } catch (err) {
      notify({ title: 'Errore', description: formatError(err, 'Discovery fallita'), variant: 'destructive' });
    } finally {
      setDiscovering(false);
    }
  }

  async function handleRegisterModel(modelName: string): Promise<void> {
    if (!selectedId) return;
    let registeredId: string | undefined;
    try {
      const res = await api.post<RegisterModelResponse>(`${basePath}/endpoints/${selectedId}/models`, {
        model_name: modelName,
      });
      registeredId = res.data?.model?.id;
      notify({ title: `Modello "${modelName}" registrato: verifica delle capability in corso.` });
      await loadModelsAndHealth(selectedId);
    } catch (err) {
      notify({ title: 'Errore', description: formatError(err, 'Registrazione modello fallita'), variant: 'destructive' });
      return;
    }
    // Le capability dichiarate di default (solo chat) vanno verificate subito:
    // stessa probe del pulsante, che aggiorna capability e finestra di contesto.
    if (registeredId) await handleProbe(registeredId);
  }

  async function handleDeleteModel(modelId: string, modelName: string): Promise<void> {
    if (!selectedId) return;
    if (!window.confirm(`Eliminare il modello "${modelName}"?`)) return;
    try {
      await api.delete(`${basePath}/endpoints/${selectedId}/models/${modelId}`);
      notify({ title: 'Modello eliminato.' });
      void loadModelsAndHealth(selectedId);
    } catch (err) {
      notify({ title: 'Errore', description: formatError(err, 'Eliminazione modello fallita'), variant: 'destructive' });
    }
  }

  function openPriceEditor(m: CustomLlmModelRow): void {
    setPriceEditId(m.id);
    setPriceDraft({
      in: m.price_in_eur_per_1k != null ? String(m.price_in_eur_per_1k) : '',
      out: m.price_out_eur_per_1k != null ? String(m.price_out_eur_per_1k) : '',
    });
  }

  async function handleSavePrices(modelId: string): Promise<void> {
    if (!selectedId) return;
    const parse = (raw: string): number | null | undefined => {
      const trimmed = raw.trim();
      if (trimmed === '') return null;
      const n = Number(trimmed.replace(',', '.'));
      return Number.isFinite(n) && n >= 0 ? n : undefined;
    };
    const priceIn = parse(priceDraft.in);
    const priceOut = parse(priceDraft.out);
    if (priceIn === undefined || priceOut === undefined) {
      notify({ title: 'Valori non validi', description: 'Prezzi EUR/1k token ≥ 0, o vuoto per rimuovere.', variant: 'destructive' });
      return;
    }
    if ((priceIn === null) !== (priceOut === null)) {
      notify({ title: 'Prezzi incompleti', description: 'Imposta entrambi i prezzi (input e output) oppure lasciali entrambi vuoti: con uno solo il costo resta non conteggiato.', variant: 'destructive' });
      return;
    }
    setSavingPrice(true);
    try {
      await api.patch(`${basePath}/endpoints/${selectedId}/models/${modelId}`, {
        price_in_eur_per_1k: priceIn,
        price_out_eur_per_1k: priceOut,
      });
      notify({ title: priceIn === null && priceOut === null ? 'Prezzi rimossi: costo non conteggiato per questo modello.' : 'Prezzi salvati.' });
      setPriceEditId(null);
      void loadModelsAndHealth(selectedId);
    } catch (err) {
      notify({ title: 'Errore', description: formatError(err, 'Salvataggio prezzi fallito'), variant: 'destructive' });
    } finally {
      setSavingPrice(false);
    }
  }

  async function handleProbe(modelId: string): Promise<void> {
    if (!selectedId) return;
    setProbingId(modelId);
    try {
      const res = await api.post<ProbeReport>(
        `${basePath}/endpoints/${selectedId}/models/${modelId}/probe`,
        {},
      );
      const probes = res.data?.probes ?? [];
      const okCount = probes.filter((p) => p.ok).length;
      notify({
        title: `Probe completata: ${okCount}/${probes.length} capability confermate (${res.data?.duration_ms ?? 0}ms)`,
      });
      void loadModelsAndHealth(selectedId);
    } catch (err) {
      notify({ title: 'Errore', description: formatError(err, 'Probe fallita'), variant: 'destructive' });
    } finally {
      setProbingId(null);
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12 text-zinc-500">
        <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Caricamento…
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="rounded-lg border border-violet-200 bg-violet-50 p-3 text-sm dark:border-violet-700 dark:bg-violet-950">
        <div className="font-semibold text-violet-900 dark:text-violet-200">Custom AI · OpenAI-compatible</div>
        <p className="mt-1 text-xs text-violet-800 dark:text-violet-300">
          Registra endpoint LLM proprietari (Ollama, vLLM, RunPod, LM Studio, Together, Groq, Mistral, qualunque
          server OpenAI-compatible). Solo HTTPS pubblico, API key cifrata at-rest, probe automatica delle capability.
        </p>
      </div>

      {showWizard ? (
        <EndpointWizard
          deps={deps}
          presets={presets}
          onCreated={() => { setShowWizard(false); void loadEndpoints(); }}
          onCancel={() => setShowWizard(false)}
        />
      ) : (
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={() => setShowWizard(true)}
            className="inline-flex items-center gap-1.5 rounded bg-sky-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-sky-700"
          >
            <Plus className="h-3 w-3" /> Nuovo endpoint
          </button>
          <button
            type="button"
            onClick={() => void loadEndpoints()}
            className="inline-flex items-center gap-1.5 rounded bg-zinc-100 px-3 py-1.5 text-xs font-medium hover:bg-zinc-200 dark:bg-zinc-800 dark:hover:bg-zinc-700"
          >
            <RefreshCw className="h-3 w-3" /> Aggiorna
          </button>
        </div>
      )}

      {endpoints.length === 0 && !showWizard && (
        <div className="rounded-lg border border-dashed border-zinc-300 p-8 text-center text-sm text-zinc-500 dark:border-zinc-700">
          Nessun endpoint custom configurato. Crea il primo per iniziare.
        </div>
      )}

      {endpoints.length > 0 && (
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
          <div className="space-y-2 lg:col-span-1">
            <h3 className="text-xs font-semibold uppercase text-zinc-500">Endpoint ({endpoints.length})</h3>
            {endpoints.map((ep) => (
              <button
                key={ep.id}
                type="button"
                onClick={() => setSelectedId(ep.id)}
                className={`w-full rounded-lg border p-3 text-left text-sm transition ${
                  selectedId === ep.id
                    ? 'border-sky-500 bg-sky-50 dark:bg-sky-950'
                    : 'border-zinc-200 hover:border-zinc-300 dark:border-zinc-700'
                }`}
              >
                <div className="flex items-center justify-between">
                  <span className="font-semibold">{ep.name}</span>
                  <span className="rounded bg-zinc-100 px-1.5 py-0.5 text-[10px] uppercase dark:bg-zinc-800">
                    {ep.kind}
                  </span>
                </div>
                <div className="mt-1 truncate text-xs text-zinc-500">{ep.base_url}</div>
                <div className="mt-1 flex items-center gap-2 text-[10px] text-zinc-400">
                  <Server className="h-3 w-3" />
                  {ep.health_status} · key {ep.api_key_hint}
                </div>
              </button>
            ))}
          </div>

          {selected && (
            <div className="space-y-4 lg:col-span-2">
              <HealthCard endpoint={selected} history={health} />

              <div className="rounded-lg border border-zinc-200 bg-white p-3 dark:border-zinc-700 dark:bg-zinc-900">
                <div className="mb-2 flex items-center justify-between">
                  <h4 className="text-sm font-semibold">Modelli registrati ({models.length})</h4>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => void handleDiscover()}
                      disabled={discovering}
                      className="inline-flex items-center gap-1 rounded bg-zinc-100 px-2 py-1 text-xs hover:bg-zinc-200 disabled:opacity-50 dark:bg-zinc-800 dark:hover:bg-zinc-700"
                    >
                      {discovering ? <Loader2 className="h-3 w-3 animate-spin" /> : <Search className="h-3 w-3" />}
                      Discover
                    </button>
                    <button
                      type="button"
                      onClick={() => void handleDelete(selected.id, selected.name)}
                      className="inline-flex items-center gap-1 rounded bg-rose-50 px-2 py-1 text-xs text-rose-700 hover:bg-rose-100 dark:bg-rose-950 dark:text-rose-300"
                    >
                      <Trash2 className="h-3 w-3" /> Elimina endpoint
                    </button>
                  </div>
                </div>

                {models.length === 0 ? (
                  <p className="text-xs text-zinc-500">
                    Nessun modello registrato. Usa “Discover” per scoprirli automaticamente da{' '}
                    <code className="rounded bg-zinc-100 px-1 dark:bg-zinc-800">/v1/models</code>.
                  </p>
                ) : (
                  <ul className="space-y-2">
                    {models.map((m) => (
                      <li
                        key={m.id}
                        className="rounded border border-zinc-100 p-2 dark:border-zinc-800"
                      >
                        <div className="flex items-center justify-between gap-2">
                          <div className="min-w-0">
                            <div className="flex items-center gap-1.5">
                              <span className="truncate text-sm font-medium">{m.display_name || m.model_name}</span>
                              {!m.available && (
                                <span
                                  className="shrink-0 rounded bg-rose-50 px-1.5 py-0.5 text-[10px] font-medium text-rose-700 dark:bg-rose-950 dark:text-rose-300"
                                  title="L'endpoint non elenca più questo modello: è escluso dal catalogo e dal routing finché non ricompare."
                                >
                                  Non più offerto
                                </span>
                              )}
                            </div>
                            <div className="truncate text-[10px] text-zinc-500">{m.model_name}</div>
                          </div>
                          <div className="flex gap-1">
                            <button
                              type="button"
                              onClick={() => void handleProbe(m.id)}
                              disabled={probingId === m.id}
                              className="inline-flex items-center gap-1 rounded bg-amber-50 px-2 py-1 text-xs text-amber-700 hover:bg-amber-100 disabled:opacity-50 dark:bg-amber-950 dark:text-amber-300"
                            >
                              {probingId === m.id ? (
                                <Loader2 className="h-3 w-3 animate-spin" />
                              ) : (
                                <Zap className="h-3 w-3" />
                              )}
                              Probe
                            </button>
                            <button
                              type="button"
                              onClick={() => void handleDeleteModel(m.id, m.model_name)}
                              className="inline-flex items-center gap-1 rounded bg-rose-50 px-2 py-1 text-xs text-rose-700 hover:bg-rose-100 dark:bg-rose-950 dark:text-rose-300"
                            >
                              <Trash2 className="h-3 w-3" />
                            </button>
                          </div>
                        </div>
                        <div className="mt-2">
                          <CapabilityChecklist capabilities={m.capabilities} compact />
                        </div>
                        {priceEditId === m.id ? (
                          <div className="mt-2 flex flex-wrap items-center gap-2">
                            <label className="flex items-center gap-1 text-[11px] text-zinc-500">
                              In €/1k
                              <input
                                type="text"
                                inputMode="decimal"
                                value={priceDraft.in}
                                onChange={(e) => setPriceDraft((d) => ({ ...d, in: e.target.value }))}
                                placeholder="es. 0.0005"
                                className="w-24 rounded border border-zinc-300 bg-white px-1.5 py-0.5 text-xs dark:border-zinc-600 dark:bg-zinc-800"
                              />
                            </label>
                            <label className="flex items-center gap-1 text-[11px] text-zinc-500">
                              Out €/1k
                              <input
                                type="text"
                                inputMode="decimal"
                                value={priceDraft.out}
                                onChange={(e) => setPriceDraft((d) => ({ ...d, out: e.target.value }))}
                                placeholder="es. 0.0015"
                                className="w-24 rounded border border-zinc-300 bg-white px-1.5 py-0.5 text-xs dark:border-zinc-600 dark:bg-zinc-800"
                              />
                            </label>
                            <button
                              type="button"
                              onClick={() => void handleSavePrices(m.id)}
                              disabled={savingPrice}
                              className="inline-flex items-center gap-1 rounded bg-emerald-600 px-2 py-1 text-xs font-medium text-white hover:bg-emerald-700 disabled:opacity-50"
                            >
                              {savingPrice ? <Loader2 className="h-3 w-3 animate-spin" /> : null}
                              Salva
                            </button>
                            <button
                              type="button"
                              onClick={() => setPriceEditId(null)}
                              className="rounded bg-zinc-100 px-2 py-1 text-xs hover:bg-zinc-200 dark:bg-zinc-800 dark:hover:bg-zinc-700"
                            >
                              Annulla
                            </button>
                          </div>
                        ) : (
                          <button
                            type="button"
                            onClick={() => openPriceEditor(m)}
                            className="mt-2 inline-flex items-center gap-1 rounded bg-zinc-50 px-2 py-1 text-[11px] text-zinc-600 hover:bg-zinc-100 dark:bg-zinc-800 dark:text-zinc-300 dark:hover:bg-zinc-700"
                            title="Prezzo opzionale usato per il conteggio costi (EUR per 1.000 token)"
                          >
                            <Euro className="h-3 w-3" />
                            {m.price_in_eur_per_1k != null || m.price_out_eur_per_1k != null
                              ? `${m.price_in_eur_per_1k ?? '—'} in / ${m.price_out_eur_per_1k ?? '—'} out €/1k`
                              : 'Prezzo non impostato — costo non conteggiato'}
                          </button>
                        )}
                        {m.last_probe_at && (
                          <div className="mt-1 text-[10px] text-zinc-400">
                            Ultima probe: {new Date(m.last_probe_at).toLocaleString('it-IT')}
                          </div>
                        )}
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              {discovered.length > 0 && (
                <div className="rounded-lg border border-zinc-200 bg-white p-3 dark:border-zinc-700 dark:bg-zinc-900">
                  <h4 className="mb-2 text-sm font-semibold">Modelli scoperti ({discovered.length})</h4>
                  <p className="mb-2 text-[10px] text-zinc-500">
                    Clicca per registrare. I modelli già registrati sono omessi.
                  </p>
                  <div className="flex flex-wrap gap-1">
                    {discovered
                      .filter((d) => !models.some((m) => m.model_name === d.id))
                      .map((d) => (
                        <button
                          key={d.id}
                          type="button"
                          onClick={() => void handleRegisterModel(d.id)}
                          className="rounded border border-zinc-200 bg-zinc-50 px-2 py-1 text-xs hover:bg-emerald-50 hover:text-emerald-700 dark:border-zinc-700 dark:bg-zinc-800"
                          title={d.owned_by ? `owned_by: ${d.owned_by}` : undefined}
                        >
                          + {d.id}
                        </button>
                      ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
